/**
 * 게임뷰 영역 좌표 — 순수 계산 (GameScene gameviewRect/playfieldBounds에서 추출, 일감 S0).
 * outer = 9-slice 프레임 전체, interior = 프레임 보더(14px) 안쪽(배경 타일·cover·발사체 반사 영역).
 */
import { LAYOUT, type ShellLayout } from "../data/balance.js";

/** 9-slice 프레임 보더 두께(px) — gameview_frame_9slice 에셋과 한 쌍. */
export const GAMEVIEW_FRAME_BORDER = 14;

/** 배경 타일은 프레임 보더 아래까지 깔아 안쪽 여백을 줄인다. */
export const GAMEVIEW_BACKGROUND_INSET = 4;

/** 게임뷰 outer 하단과 tabContentTop 사이 간격(px). outerBottom = tabContentTop - 이 값. */
export const GAMEVIEW_BOTTOM_GAP = 10;

/** 펼치기 시 게임뷰 시각 비율(상단만 남김) — 셸(ShellFrame)·전투탭(BattleTabView) 공용 SSoT(드리프트 방지). */
export const GAMEVIEW_EXPAND_FRACTION = 0.5;

export interface GameviewRect {
  outerX: number;
  outerW: number;
  outerTop: number;
  outerH: number;
  intX: number;
  intW: number;
  intTop: number;
  intH: number;
}

export interface GameviewBackgroundRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * @param layout safe-area 반영 좌표(deriveShellLayout 결과). 미지정 시 design 기준 LAYOUT 상수
 *   — 기존 호출(테스트·design 기준)과 호환. ShellFrame은 derived layout을 전달해 게임뷰가
 *   노치/홈 인디케이터를 피해 압축되게 한다.
 */
export function computeGameviewRect(
  screenWidth: number,
  layout: Pick<ShellLayout, "frameInsetX" | "gameviewTop" | "tabContentTop"> = LAYOUT,
): GameviewRect {
  const outerX = layout.frameInsetX;
  const outerW = screenWidth - layout.frameInsetX * 2;
  const outerTop = layout.gameviewTop;
  const outerBottom = layout.tabContentTop - GAMEVIEW_BOTTOM_GAP;
  const outerH = Math.max(0, outerBottom - outerTop);
  const intX = outerX + GAMEVIEW_FRAME_BORDER;
  const intW = Math.max(0, outerW - GAMEVIEW_FRAME_BORDER * 2);
  const intTop = outerTop + GAMEVIEW_FRAME_BORDER;
  const intH = Math.max(0, outerH - GAMEVIEW_FRAME_BORDER * 2);
  return { outerX, outerW, outerTop, outerH, intX, intW, intTop, intH };
}

/** 배경 전용 렌더 영역. 물리/마스크용 interior보다 넓게 잡아 프레임 안쪽을 거의 채운다. */
export function computeGameviewBackgroundRect(rect: GameviewRect): GameviewBackgroundRect {
  return {
    x: rect.outerX + GAMEVIEW_BACKGROUND_INSET,
    y: rect.outerTop + GAMEVIEW_BACKGROUND_INSET,
    width: Math.max(0, rect.outerW - GAMEVIEW_BACKGROUND_INSET * 2),
    height: Math.max(0, rect.outerH - GAMEVIEW_BACKGROUND_INSET * 2),
  };
}

/** 발사체 벽 반사 경계 = 게임뷰 interior 4면 (objects/bounceBounds PlayfieldBounds와 구조 동일). */
export function computePlayfieldBounds(rect: GameviewRect): {
  left: number;
  right: number;
  top: number;
  bottom: number;
} {
  return {
    left: rect.intX,
    right: rect.intX + rect.intW,
    top: rect.intTop,
    bottom: rect.intTop + rect.intH,
  };
}

/**
 * 전투탭 "펼치기" 시 콘텐츠 패널 상단 y — 게임뷰를 fraction(0~1) 높이로 시각 클립했을 때,
 * 축소된 게임뷰 outer 하단 바로 아래(원래 tabPanelTop↔게임뷰 하단 간격을 보존)로 패널을 끌어올린다.
 *
 * fraction=1이면 정확히 `layout.tabPanelTop`(원래 레이아웃)과 동치 — 접기 시 원복 보장.
 * deriveShellLayout이 세로 비례 압축한 layout 값을 그대로 받아 노치 기기에서도 정합한다(같은 좌표계).
 */
export function computeExpandedTop(
  layout: Pick<ShellLayout, "gameviewTop" | "tabContentTop" | "tabPanelTop">,
  fraction: number,
): number {
  const outerTop = layout.gameviewTop;
  const fullOuterBottom = layout.tabContentTop - GAMEVIEW_BOTTOM_GAP;
  const clippedOuterBottom = outerTop + (fullOuterBottom - outerTop) * fraction;
  // 원래 패널 상단과 게임뷰 outer 하단 사이 간격(=tabPanelTop-fullOuterBottom)을 클립 하단에도 동일 적용.
  const panelOffset = layout.tabPanelTop - fullOuterBottom;
  return clippedOuterBottom + panelOffset;
}
